import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function UserBanking() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");

  // User roles and permissions
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRoles, setUserRoles] = useState([]);

  // Transfer form
  const [transferForm, setTransferForm] = useState({
    recipient: "",
    searchType: "account",
    amount: "",
    description: "",
    pin: ""
  });
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [searchingRecipient, setSearchingRecipient] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [showVtuPinModal, setShowVtuPinModal] = useState(false);
  const [vtuPin, setVtuPin] = useState("");
  const [pendingVtuTransaction, setPendingVtuTransaction] = useState(null);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteForm, setDeleteForm] = useState({ password: "", reason: "" });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  // PIN settings state
  const [pinStatus, setPinStatus] = useState(null);
  const [pinForm, setPinForm] = useState({ pin: "", confirmPin: "" });
  const [changePinForm, setChangePinForm] = useState({ currentPin: "", newPin: "", confirmNewPin: "" });
  const [pinLoading, setPinLoading] = useState(false);
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordLoading, setPasswordLoading] = useState(false);
  // PIN settings state
  // Statement download state
  const [statementForm, setStatementForm] = useState({
    startDate: "",
    endDate: ""
  });
  const [downloadingStatement, setDownloadingStatement] = useState(false);
  const [statementError, setStatementError] = useState("");
  const [statementSuccess, setStatementSuccess] = useState("");

  // Bills state
  const [billType, setBillType] = useState("airtime");

  // Airtime
  const [airtimeNetworks] = useState([
    { id: "mtn", name: "MTN" },
    { id: "glo", name: "Glo" },
    { id: "airtel", name: "Airtel" },
    { id: "9mobile", name: "9mobile" }
  ]);
  const [selectedAirtimeNetwork, setSelectedAirtimeNetwork] = useState("");
  const [airtimePhone, setAirtimePhone] = useState("");
  const [airtimeAmount, setAirtimeAmount] = useState("");
  const [airtimeLoading, setAirtimeLoading] = useState(false);
  const [airtimeResult, setAirtimeResult] = useState(null);
  const [airtimeError, setAirtimeError] = useState("");

  // Data
  const [dataNetworks] = useState([
    { id: "mtn", name: "MTN" },
    { id: "glo", name: "Glo" },
    { id: "airtel", name: "Airtel" },
    { id: "9mobile", name: "9mobile" }
  ]);
  const [selectedDataNetwork, setSelectedDataNetwork] = useState("");
  const [dataPlans, setDataPlans] = useState([]);
  const [selectedDataPlan, setSelectedDataPlan] = useState("");
  const [dataPhone, setDataPhone] = useState("");
  const [dataLoading, setDataLoading] = useState(false);
  const [dataResult, setDataResult] = useState(null);
  const [dataError, setDataError] = useState("");
  
  // Electricity
  const [discos, setDiscos] = useState([]);
  const [selectedDisco, setSelectedDisco] = useState("");
  const [meterNumber, setMeterNumber] = useState("");
  const [meterType, setMeterType] = useState("prepaid");
  const [electricityAmount, setElectricityAmount] = useState("");
  const [electricityPhone, setElectricityPhone] = useState("");
  const [electricityLoading, setElectricityLoading] = useState(false);
  const [electricityResult, setElectricityResult] = useState(null);
  const [electricityError, setElectricityError] = useState("");

  // Cable TV
  const [cableProviders] = useState([
    { id: "dstv", name: "DSTV" },
    { id: "gotv", name: "GOtv" },
    { id: "startimes", name: "StarTimes" }
  ]);
  const [selectedCableProvider, setSelectedCableProvider] = useState("");
  const [cablePlans, setCablePlans] = useState([]);
  const [selectedCablePlan, setSelectedCablePlan] = useState("");
  const [smartCardNumber, setSmartCardNumber] = useState("");
  const [cableCustomerName, setCableCustomerName] = useState("");
  const [cableLoading, setCableLoading] = useState(false);
  const [cableResult, setCableResult] = useState(null);
  const [cableError, setCableError] = useState("");
  const [validatingCard, setValidatingCard] = useState(false);

  // Betting
  const [bettingProviders] = useState([
    { id: "bet9ja", name: "Bet9ja" },
    { id: "betking", name: "BetKing" },
    { id: "sportybet", name: "SportyBet" },
    { id: "1xbet", name: "1xBet" },
    { id: "betway", name: "Betway" }
  ]);
  const [selectedBettingProvider, setSelectedBettingProvider] = useState("");
  const [bettingCustomerId, setBettingCustomerId] = useState("");
  const [bettingAmount, setBettingAmount] = useState("");
  const [bettingCustomerName, setBettingCustomerName] = useState("");
  const [bettingLoading, setBettingLoading] = useState(false);
  const [bettingResult, setBettingResult] = useState(null);
  const [bettingError, setBettingError] = useState("");
  const [validatingBetting, setValidatingBetting] = useState(false);

  // Helper to normalize API response
  const normalizeWallet = (data) => {
    if (!data) return null;
    return {
      id: data.id || data.Id,
      fullName: data.fullName || data.FullName,
      balance: data.balance ?? data.Balance ?? 0,
      accountNumber: data.accountNumber || data.AccountNumber,
      status: data.status || data.Status,
      currency: data.currency || data.Currency,
      createdAt: data.createdAt || data.CreatedAt
    };
  };

  const normalizeUser = (data) => {
    if (!data) return null;
    return {
      id: data.id || data.Id,
      fullName: data.fullName || data.FullName,
      email: data.email || data.Email,
      isAdmin: data.isAdmin || data.IsAdmin,
      isAgent: data.isAgent || data.IsAgent,
      isActive: data.isActive || data.IsActive,
      createdAt: data.createdAt || data.CreatedAt,
      lastLoginAt: data.lastLoginAt || data.LastLoginAt
    };
  };

  const normalizeTransaction = (tx) => {
    if (!tx) return null;
    return {
      id: tx.id || tx.Id,
      type: tx.type || tx.Type,
      amount: tx.amount ?? tx.Amount ?? 0,
      description: tx.description || tx.Description,
      createdAt: tx.createdAt || tx.CreatedAt,
      balanceAfter: tx.balanceAfter ?? tx.BalanceAfter ?? 0
    };
  };

  const normalizeNotification = (notif) => {
    if (!notif) return null;
    return {
      id: notif.id || notif.Id,
      title: notif.title || notif.Title,
      message: notif.message || notif.Message,
      createdAt: notif.createdAt || notif.CreatedAt,
      isRead: notif.isRead || notif.IsRead
    };
  };

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const walletRes = await api.get("/Wallet/my-wallet");
      const walletData = normalizeWallet(walletRes.data);
      setWallet(walletData);

      const [txRes, notifRes] = await Promise.all([
        api.get("/Transaction/all", {
          params: { accountNumber: walletData?.accountNumber, pageSize: 10 }
        }).catch(() => ({ data: [] })),
        api.get("/Notifications").catch(() => ({ data: [] }))
      ]);

      const txData = txRes.data || [];
      setTransactions(Array.isArray(txData) ? txData.map(normalizeTransaction) : []);

      const notifData = notifRes.data || [];
      setNotifications(Array.isArray(notifData) ? notifData.map(normalizeNotification) : []);

      // Load user permissions/roles
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (storedUser.id) {
        try {
          const permRes = await api.get(`/Roles/users/${storedUser.id}/permissions`);
          setUserPermissions(permRes.data.permissionCodes || []);
          setUserRoles(permRes.data.roles || []);
          console.log("User permissions loaded:", permRes.data);
        } catch (e) {
          console.log("Could not load user permissions:", e);
        }
      }
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/user-login");
        return;
      }

      try {
        const res = await api.get("/Auth/me");
        const userData = normalizeUser(res.data);

        if (userData.isAdmin) {
          navigate("/admin");
          return;
        }

        if (userData.isAgent) {
          navigate("/agent-banking");
          return;
        }

        setUser(userData);
        loadDashboardData();
      } catch (err) {
        console.error("Auth check failed:", err);
        localStorage.removeItem("token");
        navigate("/user-login");
      }
    };

    checkUserAccess();
  }, [navigate, loadDashboardData]);

  // Initialize statement dates (default last 30 days)
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setStatementForm({
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    });
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/user-login");
  };

  // ============ BILLS FUNCTIONS ============

  // ============ AIRTIME ============
  const handleAirtimePayment = async (e) => {
    e.preventDefault();
    setAirtimeError("");
    if (!selectedAirtimeNetwork || !airtimePhone || !airtimeAmount) {
      setAirtimeError("Please fill all fields");
      return;
    }
    setPendingVtuTransaction({
      type: "airtime",
      data: { networkId: selectedAirtimeNetwork, phone: airtimePhone, amount: parseFloat(airtimeAmount) },
      description: `${parseFloat(airtimeAmount).toLocaleString()} ${selectedAirtimeNetwork.toUpperCase()} Airtime to ${airtimePhone}`
    });
    setVtuPin("");
    setShowVtuPinModal(true);
  };

  const resetAirtimeForm = () => {
    setSelectedAirtimeNetwork("");
    setAirtimePhone("");
    setAirtimeAmount("");
    setAirtimeResult(null);
    setAirtimeError("");
  };

  // ============ DATA ============
  const loadDataPlans = async (network) => {
    try {
      const res = await api.get(`/vtu/data/plans/${network}`);
      if (res.data?.success && res.data?.data) {
        setDataPlans(res.data.data);
      }
    } catch (err) {
      console.error("Failed to load data plans:", err);
      setDataPlans([]);
    }
  };


  const handleDataPayment = async (e) => {
    e.preventDefault();
    setDataError("");
    if (!selectedDataNetwork || !dataPhone || !selectedDataPlan) {
      setDataError("Please fill all fields");
      return;
    }
    const selectedPlan = dataPlans.find(p => p.variationId === selectedDataPlan);
    setPendingVtuTransaction({
      type: "data",
      data: { networkId: selectedDataNetwork, phone: dataPhone, planId: selectedDataPlan, amount: selectedPlan?.price || 0 },
      description: `${selectedPlan?.name || 'Data'} to ${dataPhone}`
    });
    setVtuPin("");
    setShowVtuPinModal(true);
  };

  const resetDataForm = () => {
    setSelectedDataNetwork("");
    setSelectedDataPlan("");
    setDataPhone("");
    setDataPlans([]);
    setDataResult(null);
    setDataError("");
  };

  // Load electricity discos
  const loadDiscos = async () => {
    try {
      const res = await api.get("/vtu/electricity/discos");
      if (res.data?.success && res.data?.data) {
        setDiscos(res.data.data);
      }
    } catch (err) {
      console.error("Failed to load discos:", err);
    }
  };

  // Pay electricity
  const handleElectricityPayment = async (e) => {
    e.preventDefault();
    setElectricityError("");
    if (!selectedDisco || !meterNumber || !electricityAmount || !electricityPhone) {
      setElectricityError("Please fill all fields");
      return;
    }
    setPendingVtuTransaction({
      type: "electricity",
      data: { discoId: selectedDisco, meterNumber: meterNumber, meterType: meterType, amount: parseFloat(electricityAmount), phone: electricityPhone },
      description: `${parseFloat(electricityAmount).toLocaleString()} Electricity for meter ${meterNumber}`
    });
    setVtuPin("");
    setShowVtuPinModal(true);
  };

  // Download electricity receipt
  const downloadReceipt = async (transactionId) => {
    try {
      const response = await api.get(`/vtu/electricity/receipt/${transactionId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `electricity-receipt-${transactionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download receipt");
    }
  };

  // Reset electricity form
  const resetElectricityForm = () => {
    setSelectedDisco("");
    setMeterNumber("");
    setMeterType("prepaid");
    setElectricityAmount("");
    setElectricityPhone("");
    setElectricityResult(null);
    setElectricityError("");
  };

  // Load cable TV plans
  const loadCablePlans = async (provider) => {
    try {
      const res = await api.get(`/vtu/cabletv/plans/${provider}`);
      if (res.data?.success && res.data?.data) {
        setCablePlans(res.data.data);
      }
    } catch (err) {
      console.error("Failed to load cable plans:", err);
      setCablePlans([]);
    }
  };

  // Validate smart card
  const validateSmartCard = async () => {
    if (!selectedCableProvider || !smartCardNumber) return;
    setValidatingCard(true);
    setCableCustomerName("");
    try {
      const res = await api.post("/vtu/cabletv/validate", {
        provider: selectedCableProvider,
        smartCardNumber: smartCardNumber
      });
      if (res.data?.success && res.data?.data?.customerName) {
        setCableCustomerName(res.data.data.customerName);
      }
    } catch (err) {
      console.error("Validation failed:", err);
    } finally {
      setValidatingCard(false);
    }
  };

  // Pay cable TV
  const handleCablePayment = async (e) => {
    e.preventDefault();
    setCableError("");
    if (!selectedCableProvider || !smartCardNumber || !selectedCablePlan) {
      setCableError("Please fill all fields");
      return;
    }
    const selectedPlan = cablePlans.find(p => p.id === selectedCablePlan);
    setPendingVtuTransaction({
      type: "cabletv",
      data: { providerId: selectedCableProvider, smartCardNumber: smartCardNumber, planId: selectedCablePlan, amount: selectedPlan?.price || 0 },
      description: `${selectedPlan?.name || 'Cable TV'} for ${smartCardNumber}`
    });
    setVtuPin("");
    setShowVtuPinModal(true);
  };

  // Reset cable form
  const resetCableForm = () => {
    setSelectedCableProvider("");
    setSelectedCablePlan("");
    setSmartCardNumber("");
    setCableCustomerName("");
    setCablePlans([]);
    setCableResult(null);
    setCableError("");
  };

  // Validate betting account
  const validateBettingAccount = async () => {
    if (!selectedBettingProvider || !bettingCustomerId) return;
    setValidatingBetting(true);
    setBettingCustomerName("");
    try {
      const res = await api.post("/vtu/betting/validate", {
        provider: selectedBettingProvider,
        customerId: bettingCustomerId
      });
      if (res.data?.success && res.data?.data?.customerName) {
        setBettingCustomerName(res.data.data.customerName);
      }
    } catch (err) {
      console.error("Validation failed:", err);
    } finally {
      setValidatingBetting(false);
    }
  };

  // Fund betting account
  const handleBettingPayment = async (e) => {
    e.preventDefault();
    setBettingError("");
    setBettingResult(null);
    setBettingLoading(true);

    try {
      const res = await api.post("/vtu/betting", {
        provider: selectedBettingProvider,
        customerId: bettingCustomerId,
        amount: parseFloat(bettingAmount)
      });

      if (res.data?.success) {
        setBettingResult(res.data.data);
        loadDashboardData();
      } else {
        setBettingError(res.data?.message || "Payment failed");
      }
    } catch (err) {
      setBettingError(err.response?.data?.message || "Payment failed. Please try again.");
    } finally {
      setBettingLoading(false);
    }
  };

  // Reset betting form
  const resetBettingForm = () => {
    setSelectedBettingProvider("");
    setBettingCustomerId("");
    setBettingAmount("");
    setBettingCustomerName("");
    setBettingResult(null);
    setBettingError("");
  };

  // VTU PIN Submit Handler
  const handleVtuPinSubmit = async () => {
    if (!vtuPin || vtuPin.length < 4) {
      alert("Please enter your 4-digit PIN");
      return;
    }

    const { type, data } = pendingVtuTransaction;

    try {
      if (type === "airtime") {
        setAirtimeLoading(true);
        const res = await api.post("/vtu/airtime", { ...data, pin: vtuPin });
        if (res.data?.success) {
          setAirtimeResult(res.data.data);
          loadDashboardData();
        } else {
          setAirtimeError(res.data?.message || "Purchase failed");
        }
        setAirtimeLoading(false);
      } else if (type === "data") {
        setDataLoading(true);
        const res = await api.post("/vtu/data", { ...data, pin: vtuPin });
        if (res.data?.success) {
          setDataResult(res.data.data);
          loadDashboardData();
        } else {
          setDataError(res.data?.message || "Purchase failed");
        }
        setDataLoading(false);
      } else if (type === "electricity") {
        setElectricityLoading(true);
        const res = await api.post("/vtu/electricity", { ...data, pin: vtuPin });
        if (res.data?.success) {
          setElectricityResult(res.data.data);
          loadDashboardData();
        } else {
          setElectricityError(res.data?.message || "Payment failed");
        }
        setElectricityLoading(false);
      } else if (type === "cabletv") {
        setCableLoading(true);
        const res = await api.post("/vtu/cabletv", { ...data, pin: vtuPin });
        if (res.data?.success) {
          setCableResult(res.data.data);
          loadDashboardData();
        } else {
          setCableError(res.data?.message || "Payment failed");
        }
        setCableLoading(false);
      } else if (type === "betting") {
        setBettingLoading(true);
        const res = await api.post("/vtu/betting", { ...data, pin: vtuPin });
        if (res.data?.success) {
          setBettingResult(res.data.data);
          loadDashboardData();
        } else {
          setBettingError(res.data?.message || "Payment failed");
        }
        setBettingLoading(false);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Transaction failed. Please try again.";
      if (type === "airtime") setAirtimeError(errorMsg);
      else if (type === "data") setDataError(errorMsg);
      else if (type === "electricity") setElectricityError(errorMsg);
      else if (type === "cabletv") setCableError(errorMsg);
      else if (type === "betting") setBettingError(errorMsg);
    } finally {
      setShowVtuPinModal(false);
      setPendingVtuTransaction(null);
      setVtuPin("");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getTransactionColor = (type) => {
    const colors = {
      "Transfer In": { bg: "#d4edda", text: "#28a745" },
      "Transfer Out": { bg: "#f8d7da", text: "#dc3545" },
      "Deposit": { bg: "#d4edda", text: "#28a745" },
      "Withdrawal": { bg: "#f8d7da", text: "#dc3545" },
      "Fee": { bg: "#fff3cd", text: "#856404" },
      "Commission": { bg: "#d1ecf1", text: "#0c5460" }
    };
    return colors[type] || { bg: "#e9ecef", text: "#666" };
  };

  // Check if user has specific permission
  const hasPermission = (permCode) => {
    return userPermissions.includes(permCode);
  };

  // Check if user has a specific role
  const hasRole = (roleName) => {
    return userRoles.some(r => r.name?.toLowerCase() === roleName.toLowerCase());
  };

  // Check if user should see special features
  const hasSpecialAccess = () => {
    return hasPermission("RISK_VIEW") || 
           hasPermission("RISK_MANAGE") || 
           hasPermission("KYC_APPROVE") ||
           hasPermission("KYC_VIEW") ||
           hasPermission("TXN_APPROVE") ||
           hasPermission("REPORT_GENERATE") ||
           hasPermission("ROLE_VIEW") ||
           hasPermission("FLOAT_VIEW") ||
           hasPermission("FLOAT_MANAGE") ||
           hasPermission("ACCT_VIEW") ||
           hasRole("Compliance Officer") ||
           hasRole("Branch Manager") ||
           hasRole("Auditor") ||
           hasRole("Teller") ||
           hasRole("Financial Control");
  };

  // FIXED: Search for recipient using /api/Wallet/lookup endpoint
  const searchRecipient = useCallback(async (value, searchType) => {
    if (!value || value.length < 3) {
      setRecipientInfo(null);
      return;
    }

    setSearchingRecipient(true);
    try {
      // Use the correct endpoint: /api/Wallet/lookup with query parameters
      const params = searchType === "email" 
        ? { email: value }
        : { accountNumber: value };

      const res = await api.get("/Wallet/lookup", { params });
      if (res.data) {
        setRecipientInfo({
          fullName: res.data.fullName || res.data.FullName,
          accountNumber: res.data.accountNumber || res.data.AccountNumber
        });
      }
    } catch (err) {
      setRecipientInfo(null);
    } finally {
      setSearchingRecipient(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (transferForm.recipient) {
        searchRecipient(transferForm.recipient, transferForm.searchType);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [transferForm.recipient, transferForm.searchType, searchRecipient]);

  const handleTransfer = async () => {
    if (!recipientInfo) {
      alert("Please enter a valid recipient");
      return;
    }
    if (!transferForm.amount || parseFloat(transferForm.amount) <= 0) {
      alert("Please enter a valid amount");
      return;
    }
    setShowPinModal(true);
  };

  const handlePinSubmit = async () => {
    if (!transferForm.pin || transferForm.pin.length < 4) {
      alert("Please enter your 4-digit PIN");
      return;
    }

    try {
      const payload = {
        toAccountNumber: recipientInfo.accountNumber,
        amount: parseFloat(transferForm.amount),
        description: transferForm.description || "Transfer",
        pin: transferForm.pin
      };

      await api.post("/Wallet/transfer", payload);
      alert("Transfer successful!");

      setTransferForm({ recipient: "", searchType: "account", amount: "", description: "", pin: "" });
      setRecipientInfo(null);
      setShowPinModal(false);
      loadDashboardData();
      setActiveTab("dashboard");
    } catch (err) {
      alert(err.response?.data?.message || "Transfer failed");
      setTransferForm({ ...transferForm, pin: "" });
    }
  };

  // Statement download functions
  const quickSelectPeriod = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStatementForm({
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    });
    setStatementError("");
    setStatementSuccess("");
  };

  const downloadStatement = async () => {
    setStatementError("");
    setStatementSuccess("");

    if (!statementForm.startDate || !statementForm.endDate) {
      setStatementError("Please select both start and end dates");
      return;
    }

    const start = new Date(statementForm.startDate);
    const end = new Date(statementForm.endDate);

    if (start > end) {
      setStatementError("Start date cannot be after end date");
      return;
    }

    const daysDiff = (end - start) / (1000 * 60 * 60 * 24);
    if (daysDiff > 365) {
      setStatementError("Date range cannot exceed 1 year");
      return;
    }

    setDownloadingStatement(true);

    try {
      const response = await api.get('/Statement/download', {
        params: { 
          startDate: statementForm.startDate, 
          endDate: statementForm.endDate 
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AccountStatement_${statementForm.startDate}_${statementForm.endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setStatementSuccess("Statement downloaded successfully!");
    } catch (err) {
      console.error("Download error:", err);
      setStatementError(err.response?.data?.message || "Failed to download statement. Please try again.");
    } finally {
      setDownloadingStatement(false);
    }
  };


  // Load PIN status on mount
  useEffect(() => {
    const loadPinStatus = async () => {
      try {
        const res = await api.get("/TwoFactor/status");
        setPinStatus(res.data);
      } catch (err) {
        console.error("Failed to load PIN status:", err);
        setPinStatus({ isEnabled: false });
      }
    };
    loadPinStatus();
  }, []);

  if (loading && !user) {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.loadingSpinner}></div>
        <p style={{ marginTop: "20px", color: "#666" }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>💰 Uptech Banking</h1>
          <span style={styles.portalBadge}>USER PORTAL</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.welcomeText}>Welcome, {user?.fullName || "User"}</span>
          <button onClick={handleLogout} style={styles.logoutBtn}>Logout</button>
        </div>
      </header>

      <nav style={styles.nav}>
        {[
          { id: "dashboard", label: "Dashboard" },
          { id: "transfer", label: "Transfer" },
          { id: "transactions", label: "Transactions" },
          { id: "bills", label: "Bills" },
          { id: "statement", label: "📄 Statement" },
          { id: "settings", label: "Settings" }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.navItem,
              ...(activeTab === tab.id ? styles.navItemActive : {})
            }}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <main style={styles.main}>
        {activeTab === "dashboard" && (
          <div style={styles.dashboard}>
            <div style={styles.accountCard}>
              <div style={styles.accountCardHeader}>
                <span style={styles.accountLabel}>Available Balance</span>
                <span style={styles.accountType}>{wallet?.currency || "NGN"}</span>
              </div>
              <div style={styles.accountBalance}>{formatCurrency(wallet?.balance || 0)}</div>
              <div style={styles.accountInfo}>
                <div>
                  <span style={styles.accountInfoLabel}>Account Number</span>
                  <span style={styles.accountNumber}>{wallet?.accountNumber || "---"}</span>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span style={styles.accountInfoLabel}>Account Name</span>
                  <span style={styles.accountName}>{wallet?.fullName || user?.fullName || "---"}</span>
                </div>
              </div>
            </div>

            <div style={styles.quickActions}>
              <h3 style={styles.sectionTitle}>Quick Actions</h3>
              <div style={styles.actionGrid}>
                <button onClick={() => setActiveTab("transfer")} style={styles.actionBtn}>
                  <span style={styles.actionIcon}>💸</span>
                  <span>Transfer</span>
                </button>
                <button onClick={() => setActiveTab("transactions")} style={styles.actionBtn}>
                  <span style={styles.actionIcon}>📋</span>
                  <span>History</span>
                </button>
                <button onClick={() => setActiveTab("statement")} style={styles.actionBtn}>
                  <span style={styles.actionIcon}>📄</span>
                  <span>Statement</span>
                </button>
                <button onClick={loadDashboardData} style={styles.actionBtn}>
                  <span style={styles.actionIcon}>🔄</span>
                  <span>Refresh</span>
                </button>
                <button onClick={() => navigate("/qr-receive")} style={{...styles.actionBtn, backgroundColor: "#e3f2fd"}}>
                  <span style={styles.actionIcon}>📱</span>
                  <span>Receive QR</span>
                </button>
                <button onClick={() => navigate("/qr-pay")} style={{...styles.actionBtn, backgroundColor: "#fce4ec"}}>
                  <span style={styles.actionIcon}>📷</span>
                  <span>Pay via QR</span>
                </button>
              </div>
            </div>

            {/* Role-Based Special Access Section */}
            {hasSpecialAccess() && (
              <div style={styles.specialAccessSection}>
                <h3 style={styles.sectionTitle}>🔐 Special Access</h3>
                {userRoles.length > 0 && (
                  <div style={styles.rolesBar}>
                    <span style={styles.rolesLabel}>Your Roles:</span>
                    {userRoles.map((role, idx) => (
                      <span key={idx} style={styles.roleBadge}>{role.name}</span>
                    ))}
                  </div>
                )}
                <div style={styles.specialActionsGrid}>
                  {(hasPermission("RISK_VIEW") || hasPermission("RISK_MANAGE") || hasRole("Compliance Officer")) && (
                    <button 
                      onClick={() => navigate("/risk-management")} 
                      style={{ ...styles.specialBtn, backgroundColor: "#dc2626" }}
                    >
                      🛡️ Risk Management
                    </button>
                  )}
                  {(hasPermission("KYC_VIEW") || hasPermission("KYC_APPROVE") || hasRole("Compliance Officer")) && (
                    <button 
                      onClick={() => navigate("/kyc-admin")} 
                      style={{ ...styles.specialBtn, backgroundColor: "#f59e0b" }}
                    >
                      📋 KYC Review
                    </button>
                  )}
                  {(hasPermission("REPORT_GENERATE") || hasPermission("REPORT_VIEW") || hasRole("Compliance Officer") || hasRole("Auditor")) && (
                    <button 
                      onClick={() => navigate("/reports")} 
                      style={{ ...styles.specialBtn, backgroundColor: "#6366f1" }}
                    >
                      📈 Reports
                    </button>
                  )}
                  {(hasPermission("TXN_APPROVE") || hasRole("Branch Manager")) && (
                    <button 
                      onClick={() => navigate("/transaction-approvals")} 
                      style={{ ...styles.specialBtn, backgroundColor: "#0ea5e9" }}
                    >
                      ✅ Approvals
                    </button>
                  )}
                  {(hasPermission("ROLE_VIEW") || hasPermission("ROLE_ASSIGN")) && (
                    <button 
                      onClick={() => navigate("/role-management")} 
                      style={{ ...styles.specialBtn, backgroundColor: "#8b5cf6" }}
                    >
                      👥 Roles
                    </button>
                  )}
                  {(hasPermission("FLOAT_VIEW") || hasPermission("FLOAT_MANAGE") || hasPermission("ACCT_VIEW") || hasRole("Teller") || hasRole("Branch Manager") || hasRole("Financial Control")) && (
                    <button 
                      onClick={() => navigate("/float-management")} 
                      style={{ ...styles.specialBtn, backgroundColor: "#0891b2" }}
                    >
                      💵 Agent Float
                    </button>
                  )}
                </div>
              </div>
            )}

            <div style={styles.recentSection}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Recent Transactions</h3>
                <button onClick={() => setActiveTab("transactions")} style={styles.viewAllBtn}>View All</button>
              </div>
              {transactions.length === 0 ? (
                <div style={styles.noData}>No transactions yet</div>
              ) : (
                <div style={styles.transactionList}>
                  {transactions.slice(0, 5).map(tx => {
                    const colors = getTransactionColor(tx.type);
                    return (
                      <div key={tx.id} style={styles.transactionItem}>
                        <div style={styles.txLeft}>
                          <div style={{ ...styles.txIcon, backgroundColor: colors.bg, color: colors.text }}>
                            {tx.type?.includes("In") || tx.type === "Deposit" ? "+" : "-"}
                          </div>
                          <div>
                            <div style={styles.txDescription}>{tx.description || tx.type}</div>
                            <div style={styles.txDate}>{formatDate(tx.createdAt)}</div>
                          </div>
                        </div>
                        <div style={{ ...styles.txAmount, color: colors.text }}>
                          {tx.amount >= 0 ? "+" : ""}{formatCurrency(tx.amount)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "transfer" && (
          <div style={styles.transferSection}>
            <h2 style={styles.pageTitle}>💸 Send Money</h2>
            <div style={styles.transferCard}>
              <div style={styles.balanceReminder}>
                Available Balance: <strong>{formatCurrency(wallet?.balance || 0)}</strong>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Search By</label>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input type="radio" name="searchType" value="account"
                      checked={transferForm.searchType === "account"}
                      onChange={(e) => {
                        setTransferForm({ ...transferForm, searchType: e.target.value, recipient: "" });
                        setRecipientInfo(null);
                      }}
                    /> Account Number
                  </label>
                  <label style={styles.radioLabel}>
                    <input type="radio" name="searchType" value="email"
                      checked={transferForm.searchType === "email"}
                      onChange={(e) => {
                        setTransferForm({ ...transferForm, searchType: e.target.value, recipient: "" });
                        setRecipientInfo(null);
                      }}
                    /> Email
                  </label>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {transferForm.searchType === "email" ? "Recipient Email" : "Account Number"}
                </label>
                <div style={styles.inputWrapper}>
                  <input
                    type={transferForm.searchType === "email" ? "email" : "text"}
                    value={transferForm.recipient}
                    onChange={(e) => setTransferForm({ ...transferForm, recipient: e.target.value })}
                    placeholder={transferForm.searchType === "email" ? "Enter email address" : "Enter account number"}
                    style={styles.input}
                  />
                  {searchingRecipient && (
                    <div style={styles.searchingIndicator}><div style={styles.spinner}></div></div>
                  )}
                </div>
                <p style={styles.helperText}>Enter at least 3 characters to search</p>
              </div>

              {recipientInfo && (
                <div style={styles.recipientCard}>
                  <div style={styles.recipientIcon}>✓</div>
                  <div>
                    <div style={styles.recipientName}>{recipientInfo.fullName}</div>
                    <div style={styles.recipientAccount}>{recipientInfo.accountNumber}</div>
                  </div>
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>Amount (NGN)</label>
                <input type="number" value={transferForm.amount}
                  onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                  placeholder="Enter amount" style={styles.input} min="1"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description (Optional)</label>
                <input type="text" value={transferForm.description}
                  onChange={(e) => setTransferForm({ ...transferForm, description: e.target.value })}
                  placeholder="What's this for?" style={styles.input}
                />
              </div>

              <button onClick={handleTransfer}
                style={{
                  ...styles.transferBtn,
                  opacity: (!recipientInfo || !transferForm.amount) ? 0.6 : 1,
                  cursor: (!recipientInfo || !transferForm.amount) ? "not-allowed" : "pointer"
                }}
                disabled={!recipientInfo || !transferForm.amount}
              >
                Continue to Transfer
              </button>
            </div>
          </div>
        )}

        {activeTab === "transactions" && (
          <div style={styles.transactionsSection}>
            <h2 style={styles.pageTitle}>📋 Transaction History</h2>
            <div style={styles.transactionsCard}>
              {transactions.length === 0 ? (
                <div style={styles.noData}>No transactions found</div>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Description</th>
                      <th style={styles.th}>Amount</th>
                      <th style={styles.th}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(tx => {
                      const colors = getTransactionColor(tx.type);
                      return (
                        <tr key={tx.id}>
                          <td style={styles.td}>{formatDate(tx.createdAt)}</td>
                          <td style={styles.td}>
                            <span style={{ ...styles.typeBadge, backgroundColor: colors.bg, color: colors.text }}>
                              {tx.type}
                            </span>
                          </td>
                          <td style={styles.td}>{tx.description}</td>
                          <td style={{ ...styles.td, color: colors.text, fontWeight: "600" }}>
                            {tx.amount >= 0 ? "+" : ""}{formatCurrency(tx.amount)}
                          </td>
                          <td style={styles.td}>{formatCurrency(tx.balanceAfter)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* NEW: Statement Tab */}
        {activeTab === "statement" && (
          <div style={styles.statementSection}>
            <h2 style={styles.pageTitle}>📄 Account Statement</h2>
            <p style={styles.pageSubtitle}>Download your transaction history as a PDF document</p>
            
            <div style={styles.statementCard}>
              {/* Quick Select Buttons */}
              <div style={styles.quickSelectSection}>
                <label style={styles.label}>Quick Select Period:</label>
                <div style={styles.quickSelectGrid}>
                  <button onClick={() => quickSelectPeriod(7)} style={styles.quickSelectBtn}>Last 7 Days</button>
                  <button onClick={() => quickSelectPeriod(30)} style={styles.quickSelectBtn}>Last 30 Days</button>
                  <button onClick={() => quickSelectPeriod(90)} style={styles.quickSelectBtn}>Last 3 Months</button>
                  <button onClick={() => quickSelectPeriod(180)} style={styles.quickSelectBtn}>Last 6 Months</button>
                  <button onClick={() => quickSelectPeriod(365)} style={styles.quickSelectBtn}>Last Year</button>
                </div>
              </div>

              {/* Date Inputs */}
              <div style={styles.dateRow}>
                <div style={styles.dateField}>
                  <label style={styles.label}>Start Date</label>
                  <input
                    type="date"
                    value={statementForm.startDate}
                    onChange={(e) => {
                      setStatementForm({ ...statementForm, startDate: e.target.value });
                      setStatementError("");
                      setStatementSuccess("");
                    }}
                    style={styles.input}
                  />
                </div>
                <div style={styles.dateField}>
                  <label style={styles.label}>End Date</label>
                  <input
                    type="date"
                    value={statementForm.endDate}
                    onChange={(e) => {
                      setStatementForm({ ...statementForm, endDate: e.target.value });
                      setStatementError("");
                      setStatementSuccess("");
                    }}
                    max={new Date().toISOString().split('T')[0]}
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Error Message */}
              {statementError && (
                <div style={styles.errorBox}>
                  ⚠️ {statementError}
                </div>
              )}

              {/* Success Message */}
              {statementSuccess && (
                <div style={styles.successBox}>
                  ✅ {statementSuccess}
                </div>
              )}

              {/* Download Button */}
              <button
                onClick={downloadStatement}
                disabled={downloadingStatement}
                style={{
                  ...styles.downloadBtn,
                  opacity: downloadingStatement ? 0.7 : 1,
                  cursor: downloadingStatement ? "not-allowed" : "pointer"
                }}
              >
                {downloadingStatement ? (
                  <>
                    <span style={styles.btnSpinner}></span>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    📥 Download Statement
                  </>
                )}
              </button>

              <p style={styles.statementNote}>
                Your statement will include all transactions within the selected date range, 
                along with opening and closing balances.
              </p>
            </div>
          </div>
        )}

        {activeTab === "bills" && (
          <div style={styles.billsSection}>
            <h2 style={styles.pageTitle}> Pay Bills</h2>
            
            {/* Bill Type Tabs */}
            <div style={styles.billTabs}>
              <button
                onClick={() => setBillType("airtime")}
                style={{...styles.billTab, ...(billType === "airtime" ? styles.billTabActive : {})}}
              >
                 Airtime
              </button>
              <button
                onClick={() => setBillType("data")}
                style={{...styles.billTab, ...(billType === "data" ? styles.billTabActive : {})}}
              >
                 Data
              </button>
              <button
                onClick={() => { setBillType("electricity"); loadDiscos(); }}
                style={{...styles.billTab, ...(billType === "electricity" ? styles.billTabActive : {})}}
              >
                 Electricity
              </button>
              <button
                onClick={() => setBillType("cabletv")}
                style={{...styles.billTab, ...(billType === "cabletv" ? styles.billTabActive : {})}}
              >
                 Cable TV
              </button>
              <button
                onClick={() => setBillType("betting")}
                style={{...styles.billTab, ...(billType === "betting" ? styles.billTabActive : {})}}
              >
                 Betting
              </button>
            </div>

            {/* Electricity Form */}
            {billType === "airtime" && !airtimeResult && (
              <div style={styles.billCard}>
                <h3 style={styles.billCardTitle}>Buy Airtime</h3>
                {airtimeError && <div style={styles.errorBox}>{airtimeError}</div>}
                <form onSubmit={handleAirtimePayment}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Network</label>
                    <select style={styles.input} value={selectedAirtimeNetwork} onChange={(e) => setSelectedAirtimeNetwork(e.target.value)} required>
                      <option value="">Select Network</option>
                      {airtimeNetworks.map(n => (<option key={n.id} value={n.id}>{n.name}</option>))}
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Phone Number</label>
                    <input type="tel" style={styles.input} value={airtimePhone} onChange={(e) => setAirtimePhone(e.target.value)} placeholder="08012345678" required />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Amount ()</label>
                    <input type="number" style={styles.input} value={airtimeAmount} onChange={(e) => setAirtimeAmount(e.target.value)} placeholder="Enter amount" min="50" required />
                  </div>
                  <button type="submit" style={styles.payBtn} disabled={airtimeLoading}>{airtimeLoading ? "Processing..." : "Buy Airtime"}</button>
                </form>
              </div>
            )}

            {billType === "airtime" && airtimeResult && (
              <div style={styles.successCard}>
                <div style={styles.successIcon}></div>
                <h3 style={styles.successTitle}>Airtime Purchased!</h3>
                <div style={styles.resultDetails}>
                  <div style={styles.resultRow}><span>Network:</span><strong>{airtimeResult.network?.toUpperCase()}</strong></div>
                  <div style={styles.resultRow}><span>Phone:</span><strong>{airtimeResult.phone}</strong></div>
                  <div style={styles.resultRow}><span>Amount:</span><strong>{airtimeResult.amount?.toLocaleString()}</strong></div>
                </div>
                <button onClick={resetAirtimeForm} style={styles.newPaymentBtn}>Buy More Airtime</button>
              </div>
            )}

            {billType === "data" && !dataResult && (
              <div style={styles.billCard}>
                <h3 style={styles.billCardTitle}>Buy Data</h3>
                {dataError && <div style={styles.errorBox}>{dataError}</div>}
                <form onSubmit={handleDataPayment}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Network</label>
                    <select style={styles.input} value={selectedDataNetwork} onChange={(e) => { setSelectedDataNetwork(e.target.value); setSelectedDataPlan(""); if (e.target.value) loadDataPlans(e.target.value); }} required>
                      <option value="">Select Network</option>
                      {dataNetworks.map(n => (<option key={n.id} value={n.id}>{n.name}</option>))}
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Data Plan</label>
                    <select style={styles.input} value={selectedDataPlan} onChange={(e) => setSelectedDataPlan(e.target.value)} required disabled={!selectedDataNetwork}>
                      <option value="">Select Plan</option>
                      {dataPlans.map(plan => (<option key={plan.id} value={plan.id}>{plan.name} - {plan.price?.toLocaleString()}</option>))}
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Phone Number</label>
                    <input type="tel" style={styles.input} value={dataPhone} onChange={(e) => setDataPhone(e.target.value)} placeholder="08012345678" required />
                  </div>
                  <button type="submit" style={styles.payBtn} disabled={dataLoading}>{dataLoading ? "Processing..." : "Buy Data"}</button>
                </form>
              </div>
            )}

            {billType === "data" && dataResult && (
              <div style={styles.successCard}>
                <div style={styles.successIcon}></div>
                <h3 style={styles.successTitle}>Data Purchased!</h3>
                <div style={styles.resultDetails}>
                  <div style={styles.resultRow}><span>Network:</span><strong>{dataResult.network?.toUpperCase()}</strong></div>
                  <div style={styles.resultRow}><span>Phone:</span><strong>{dataResult.phone}</strong></div>
                  <div style={styles.resultRow}><span>Plan:</span><strong>{dataResult.planName}</strong></div>
                  <div style={styles.resultRow}><span>Amount:</span><strong>{dataResult.amount?.toLocaleString()}</strong></div>
                </div>
                <button onClick={resetDataForm} style={styles.newPaymentBtn}>Buy More Data</button>
              </div>
            )}

            {billType === "electricity" && !electricityResult && (
              <div style={styles.billCard}>
                <h3 style={styles.billCardTitle}>Buy Electricity Token</h3>
                {electricityError && <div style={styles.errorBox}>{electricityError}</div>}
                
                <form onSubmit={handleElectricityPayment}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Distribution Company</label>
                    <select 
                      style={styles.input}
                      value={selectedDisco}
                      onChange={(e) => setSelectedDisco(e.target.value)}
                      required
                    >
                      <option value="">Select DisCo</option>
                      {discos.map(disco => (
                        <option key={disco.id} value={disco.id}>{disco.name}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Meter Type</label>
                    <div style={styles.radioGroup}>
                      <label style={styles.radioLabel}>
                        <input 
                          type="radio" 
                          name="meterType" 
                          value="prepaid"
                          checked={meterType === "prepaid"}
                          onChange={(e) => setMeterType(e.target.value)}
                        />
                        Prepaid
                      </label>
                      <label style={styles.radioLabel}>
                        <input 
                          type="radio" 
                          name="meterType" 
                          value="postpaid"
                          checked={meterType === "postpaid"}
                          onChange={(e) => setMeterType(e.target.value)}
                        />
                        Postpaid
                      </label>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Meter Number</label>
                    <input 
                      type="text"
                      style={styles.input}
                      value={meterNumber}
                      onChange={(e) => setMeterNumber(e.target.value)}
                      placeholder="Enter meter number"
                      required
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Amount ()</label>
                    <input 
                      type="number"
                      style={styles.input}
                      value={electricityAmount}
                      onChange={(e) => setElectricityAmount(e.target.value)}
                      placeholder="Min 1,000"
                      min="1000"
                      required
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Phone Number (for token SMS)</label>
                    <input 
                      type="tel"
                      style={styles.input}
                      value={electricityPhone}
                      onChange={(e) => setElectricityPhone(e.target.value)}
                      placeholder="08012345678"
                      required
                    />
                  </div>

                  <button 
                    type="submit" 
                    style={styles.payBtn}
                    disabled={electricityLoading}
                  >
                    {electricityLoading ? "Processing..." : "Buy Token"}
                  </button>
                </form>
              </div>
            )}

            {/* Electricity Success */}
            {billType === "electricity" && electricityResult && (
              <div style={styles.successCard}>
                <div style={styles.successIcon}></div>
                <h3 style={styles.successTitle}>Payment Successful!</h3>
                
                <div style={styles.tokenDisplay}>
                  <span style={styles.tokenLabel}>Your Electricity Token</span>
                  <span style={styles.tokenValue}>{electricityResult.token || "N/A"}</span>
                </div>

                <div style={styles.resultDetails}>
                  <div style={styles.resultRow}>
                    <span>Customer Name:</span>
                    <strong>{electricityResult.customerName || "N/A"}</strong>
                  </div>
                  <div style={styles.resultRow}>
                    <span>Address:</span>
                    <strong>{electricityResult.customerAddress || "N/A"}</strong>
                  </div>
                  <div style={styles.resultRow}>
                    <span>Meter Number:</span>
                    <strong>{electricityResult.meterNumber}</strong>
                  </div>
                  <div style={styles.resultRow}>
                    <span>Amount:</span>
                    <strong>{electricityResult.amount?.toLocaleString()}</strong>
                  </div>
                  <div style={styles.resultRow}>
                    <span>Units:</span>
                    <strong>{electricityResult.units || "N/A"}</strong>
                  </div>
                </div>

                <div style={styles.successActions}>
                  <button 
                    onClick={() => downloadReceipt(electricityResult.transactionId)}
                    style={styles.downloadBtn}
                  >
                     Download Receipt
                  </button>
                  <button 
                    onClick={resetElectricityForm}
                    style={styles.newPaymentBtn}
                  >
                    New Payment
                  </button>
                </div>
              </div>
            )}

            {/* Cable TV Form */}
            {billType === "cabletv" && !cableResult && (
              <div style={styles.billCard}>
                <h3 style={styles.billCardTitle}>Cable TV Subscription</h3>
                {cableError && <div style={styles.errorBox}>{cableError}</div>}
                
                <form onSubmit={handleCablePayment}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Provider</label>
                    <select 
                      style={styles.input}
                      value={selectedCableProvider}
                      onChange={(e) => {
                        setSelectedCableProvider(e.target.value);
                        setSelectedCablePlan("");
                        setCableCustomerName("");
                        if (e.target.value) loadCablePlans(e.target.value);
                      }}
                      required
                    >
                      <option value="">Select Provider</option>
                      {cableProviders.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Smart Card / IUC Number</label>
                    <div style={styles.inputWrapper}>
                      <input 
                        type="text"
                        style={styles.input}
                        value={smartCardNumber}
                        onChange={(e) => setSmartCardNumber(e.target.value)}
                        onBlur={validateSmartCard}
                        placeholder="Enter smart card number"
                        required
                      />
                      {validatingCard && <div style={styles.searchingIndicator}><div style={styles.spinner}></div></div>}
                    </div>
                    {cableCustomerName && (
                      <div style={styles.recipientCard}>
                        <div style={styles.recipientIcon}></div>
                        <div>
                          <div style={styles.recipientName}>{cableCustomerName}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Package</label>
                    <select 
                      style={styles.input}
                      value={selectedCablePlan}
                      onChange={(e) => setSelectedCablePlan(e.target.value)}
                      required
                      disabled={!selectedCableProvider}
                    >
                      <option value="">Select Package</option>
                      {cablePlans.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} - {plan.price?.toLocaleString()}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button 
                    type="submit" 
                    style={styles.payBtn}
                    disabled={cableLoading}
                  >
                    {cableLoading ? "Processing..." : "Subscribe"}
                  </button>
                </form>
              </div>
            )}

            {/* Cable TV Success */}
            {billType === "cabletv" && cableResult && (
              <div style={styles.successCard}>
                <div style={styles.successIcon}></div>
                <h3 style={styles.successTitle}>Subscription Successful!</h3>
                
                <div style={styles.resultDetails}>
                  <div style={styles.resultRow}>
                    <span>Customer:</span>
                    <strong>{cableResult.customerName || "N/A"}</strong>
                  </div>
                  <div style={styles.resultRow}>
                    <span>Smart Card:</span>
                    <strong>{cableResult.smartCardNumber}</strong>
                  </div>
                  <div style={styles.resultRow}>
                    <span>Package:</span>
                    <strong>{cableResult.planName}</strong>
                  </div>
                  <div style={styles.resultRow}>
                    <span>Amount:</span>
                    <strong>{cableResult.amount?.toLocaleString()}</strong>
                  </div>
                </div>

                <button 
                  onClick={resetCableForm}
                  style={styles.newPaymentBtn}
                >
                  New Subscription
                </button>
              </div>
            )}

            {/* Betting Form */}
            {billType === "betting" && !bettingResult && (
              <div style={styles.billCard}>
                <h3 style={styles.billCardTitle}>Fund Betting Account</h3>
                {bettingError && <div style={styles.errorBox}>{bettingError}</div>}
                
                <form onSubmit={handleBettingPayment}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Betting Platform</label>
                    <select 
                      style={styles.input}
                      value={selectedBettingProvider}
                      onChange={(e) => {
                        setSelectedBettingProvider(e.target.value);
                        setBettingCustomerName("");
                      }}
                      required
                    >
                      <option value="">Select Platform</option>
                      {bettingProviders.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Customer ID / User ID</label>
                    <div style={styles.inputWrapper}>
                      <input 
                        type="text"
                        style={styles.input}
                        value={bettingCustomerId}
                        onChange={(e) => setBettingCustomerId(e.target.value)}
                        onBlur={validateBettingAccount}
                        placeholder="Enter your betting ID"
                        required
                      />
                      {validatingBetting && <div style={styles.searchingIndicator}><div style={styles.spinner}></div></div>}
                    </div>
                    {bettingCustomerName && (
                      <div style={styles.recipientCard}>
                        <div style={styles.recipientIcon}></div>
                        <div>
                          <div style={styles.recipientName}>{bettingCustomerName}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Amount ()</label>
                    <input 
                      type="number"
                      style={styles.input}
                      value={bettingAmount}
                      onChange={(e) => setBettingAmount(e.target.value)}
                      placeholder="Enter amount"
                      min="100"
                      required
                    />
                  </div>

                  <button 
                    type="submit" 
                    style={styles.payBtn}
                    disabled={bettingLoading || !bettingCustomerName}
                  >
                    {bettingLoading ? "Processing..." : "Fund Account"}
                  </button>
                </form>
              </div>
            )}

            {/* Betting Success */}
            {billType === "betting" && bettingResult && (
              <div style={styles.successCard}>
                <div style={styles.successIcon}></div>
                <h3 style={styles.successTitle}>Funding Successful!</h3>
                
                <div style={styles.resultDetails}>
                  <div style={styles.resultRow}>
                    <span>Platform:</span>
                    <strong>{bettingResult.provider}</strong>
                  </div>
                  <div style={styles.resultRow}>
                    <span>Customer ID:</span>
                    <strong>{bettingResult.customerId}</strong>
                  </div>
                  <div style={styles.resultRow}>
                    <span>Amount:</span>
                    <strong>{bettingResult.amount?.toLocaleString()}</strong>
                  </div>
                </div>

                <button 
                  onClick={resetBettingForm}
                  style={styles.newPaymentBtn}
                >
                  New Funding
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === "settings" && (
          <div style={styles.settingsSection}>
            <h2 style={styles.pageTitle}>⚙️ Account Settings</h2>
            
            {/* Transaction PIN Settings Card */}
            <div style={styles.settingsCard}>
              <h4 style={styles.settingsTitle}>🔐 Transaction PIN</h4>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}>
                <span>PIN Status:</span>
                <span style={{
                  padding: "4px 12px",
                  borderRadius: "12px",
                  fontSize: "13px",
                  fontWeight: "600",
                  backgroundColor: pinStatus?.isEnabled ? "#d4edda" : "#f8d7da",
                  color: pinStatus?.isEnabled ? "#155724" : "#721c24"
                }}>
                  {pinStatus?.isEnabled ? "✅ Enabled" : "❌ Not Set"}
                </span>
              </div>
              {pinStatus?.isLocked && (
                <div style={{ background: "#f8d7da", color: "#721c24", padding: "10px", borderRadius: "8px", marginBottom: "15px", fontSize: "14px" }}>
                  ⚠️ Account is locked due to too many failed attempts. Contact support.
                </div>
              )}

              {/* Setup PIN Form (if not enabled) */}
              {!pinStatus?.isEnabled && (
                <div>
                  <p style={{ color: "#666", fontSize: "14px", marginBottom: "15px" }}>Create a 4-6 digit PIN to authorize transactions.</p>
                  <div style={{ marginBottom: "12px" }}>
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px", color: "#333" }}>Enter PIN (4-6 digits)</label>
                    <input
                      type="password"
                      value={pinForm.pin}
                      onChange={(e) => setPinForm({...pinForm, pin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      placeholder="Enter PIN"
                      maxLength={6}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "16px", letterSpacing: "8px", textAlign: "center", boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ marginBottom: "15px" }}>
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px", color: "#333" }}>Confirm PIN</label>
                    <input
                      type="password"
                      value={pinForm.confirmPin}
                      onChange={(e) => setPinForm({...pinForm, confirmPin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      placeholder="Confirm PIN"
                      maxLength={6}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "16px", letterSpacing: "8px", textAlign: "center", boxSizing: "border-box" }}
                    />
                  </div>
                  <button
                    onClick={async () => {
                      if (!pinForm.pin || pinForm.pin.length < 4 || pinForm.pin.length > 6) { alert("PIN must be 4-6 digits"); return; }
                      if (pinForm.pin !== pinForm.confirmPin) { alert("PINs do not match"); return; }
                      if (!/^\d+$/.test(pinForm.pin)) { alert("PIN must contain only numbers"); return; }
                      setPinLoading(true);
                      try {
                        await api.post("/TwoFactor/setup", { pin: pinForm.pin, confirmPin: pinForm.confirmPin });
                        alert("Transaction PIN set up successfully!");
                        setPinForm({ pin: "", confirmPin: "" });
                        try { const res = await api.get("/TwoFactor/status"); setPinStatus(res.data); } catch(e) {}
                      } catch (err) { alert(err.response?.data?.message || "Failed to set up PIN"); }
                      finally { setPinLoading(false); }
                    }}
                    style={{ width: "100%", padding: "12px", backgroundColor: "#4f46e5", color: "white", border: "none", borderRadius: "8px", fontWeight: "600", fontSize: "15px", cursor: "pointer", opacity: pinLoading ? 0.6 : 1 }}
                    disabled={pinLoading}
                  >
                    {pinLoading ? "Setting up..." : "🔒 Set Up PIN"}
                  </button>
                </div>
              )}

              {/* Change PIN Form (if enabled) */}
              {pinStatus?.isEnabled && (
                <div>
                  <p style={{ color: "#666", fontSize: "14px", marginBottom: "15px" }}>Change your existing transaction PIN.</p>
                  <div style={{ marginBottom: "12px" }}>
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px", color: "#333" }}>Current PIN</label>
                    <input
                      type="password"
                      value={changePinForm.currentPin}
                      onChange={(e) => setChangePinForm({...changePinForm, currentPin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      placeholder="Current PIN"
                      maxLength={6}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "16px", letterSpacing: "8px", textAlign: "center", boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ marginBottom: "12px" }}>
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px", color: "#333" }}>New PIN</label>
                    <input
                      type="password"
                      value={changePinForm.newPin}
                      onChange={(e) => setChangePinForm({...changePinForm, newPin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      placeholder="New PIN"
                      maxLength={6}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "16px", letterSpacing: "8px", textAlign: "center", boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ marginBottom: "15px" }}>
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px", color: "#333" }}>Confirm New PIN</label>
                    <input
                      type="password"
                      value={changePinForm.confirmNewPin}
                      onChange={(e) => setChangePinForm({...changePinForm, confirmNewPin: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      placeholder="Confirm New PIN"
                      maxLength={6}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "16px", letterSpacing: "8px", textAlign: "center", boxSizing: "border-box" }}
                    />
                  </div>
                  <button
                    onClick={async () => {
                      if (!changePinForm.currentPin) { alert("Please enter your current PIN"); return; }
                      if (!changePinForm.newPin || changePinForm.newPin.length < 4 || changePinForm.newPin.length > 6) { alert("New PIN must be 4-6 digits"); return; }
                      if (changePinForm.newPin !== changePinForm.confirmNewPin) { alert("New PINs do not match"); return; }
                      if (!/^\d+$/.test(changePinForm.newPin)) { alert("PIN must contain only numbers"); return; }
                      setPinLoading(true);
                      try {
                        await api.post("/TwoFactor/change", { currentPin: changePinForm.currentPin, newPin: changePinForm.newPin, confirmNewPin: changePinForm.confirmNewPin });
                        alert("Transaction PIN changed successfully!");
                        setChangePinForm({ currentPin: "", newPin: "", confirmNewPin: "" });
                      } catch (err) { alert(err.response?.data?.message || "Failed to change PIN"); }
                      finally { setPinLoading(false); }
                    }}
                    style={{ width: "100%", padding: "12px", backgroundColor: "#f59e0b", color: "white", border: "none", borderRadius: "8px", fontWeight: "600", fontSize: "15px", cursor: "pointer", opacity: pinLoading ? 0.6 : 1 }}
                    disabled={pinLoading}
                  >
                    {pinLoading ? "Changing..." : "🔄 Change PIN"}
                  </button>
                </div>
              )}
            </div>
            
            <div style={styles.settingsCard}>
              <h4 style={styles.settingsTitle}>Profile Information</h4>
              <div style={styles.settingsItem}>
                <span style={styles.settingsLabel}>Full Name</span>
                <span style={styles.settingsValue}>{user?.fullName}</span>
              </div>
              <div style={styles.settingsItem}>
                <span style={styles.settingsLabel}>Email</span>
                <span style={styles.settingsValue}>{user?.email}</span>
              </div>
              <div style={styles.settingsItem}>
                <span style={styles.settingsLabel}>Account Status</span>
                <span style={{ ...styles.statusBadge, backgroundColor: "#d4edda", color: "#28a745" }}>
                  {wallet?.status || "Active"}
                </span>
              </div>
              <div style={styles.settingsItem}>
                <span style={styles.settingsLabel}>Member Since</span>
                <span style={styles.settingsValue}>{user?.createdAt ? formatDate(user.createdAt) : "---"}</span>
              </div>
            </div>
            <div style={styles.settingsCard}>
              <h4 style={styles.settingsTitle}>🔒 Security</h4>
              {!showChangePassword ? (
                <button
                  onClick={() => setShowChangePassword(true)}
                  style={{ width: "100%", padding: "12px", backgroundColor: "#4f46e5", color: "white", border: "none", borderRadius: "8px", fontWeight: "600", fontSize: "15px", cursor: "pointer" }}
                >
                  Change Password
                </button>
              ) : (
                <div>
                  <div style={{ marginBottom: "12px" }}>
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px", color: "#333" }}>Current Password</label>
                    <input
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                      placeholder="Enter current password"
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ marginBottom: "12px" }}>
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px", color: "#333" }}>New Password</label>
                    <input
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                      placeholder="Enter new password"
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ marginBottom: "15px" }}>
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "600", fontSize: "14px", color: "#333" }}>Confirm New Password</label>
                    <input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                      placeholder="Confirm new password"
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={() => { setShowChangePassword(false); setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" }); }}
                      style={{ flex: 1, padding: "12px", backgroundColor: "#e5e7eb", color: "#333", border: "none", borderRadius: "8px", fontWeight: "600", fontSize: "14px", cursor: "pointer" }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        if (!passwordForm.currentPassword) { alert("Please enter your current password"); return; }
                        if (!passwordForm.newPassword || passwordForm.newPassword.length < 6) { alert("New password must be at least 6 characters"); return; }
                        if (passwordForm.newPassword !== passwordForm.confirmPassword) { alert("New passwords do not match"); return; }
                        setPasswordLoading(true);
                        try {
                          await api.post("/Auth/change-password", { currentPassword: passwordForm.currentPassword, newPassword: passwordForm.newPassword });
                          alert("Password changed successfully!");
                          setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
                          setShowChangePassword(false);
                        } catch (err) { alert(err.response?.data?.message || "Failed to change password"); }
                        finally { setPasswordLoading(false); }
                      }}
                      style={{ flex: 1, padding: "12px", backgroundColor: "#4f46e5", color: "white", border: "none", borderRadius: "8px", fontWeight: "600", fontSize: "14px", cursor: "pointer", opacity: passwordLoading ? 0.6 : 1 }}
                      disabled={passwordLoading}
                    >
                      {passwordLoading ? "Changing..." : "Update Password"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {showPinModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>🔐 Enter Transaction PIN</h3>
            <p style={styles.modalSubtitle}>
              Transfer {formatCurrency(Number(transferForm.amount))} to {recipientInfo?.fullName}
            </p>
            <input type="password" value={transferForm.pin}
              onChange={(e) => setTransferForm({ ...transferForm, pin: e.target.value })}
              placeholder="Enter 4-digit PIN" maxLength={6} style={styles.pinInput} autoFocus
            />
            <div style={styles.modalButtons}>
              <button onClick={() => { setShowPinModal(false); setTransferForm({ ...transferForm, pin: "" }); }} style={styles.cancelBtn}>
                Cancel
              </button>
              <button onClick={handlePinSubmit} style={styles.confirmBtn}>
                Confirm Transfer
              </button>
            </div>
          </div>
        </div>
      )}

      {showVtuPinModal && pendingVtuTransaction && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}> Enter Transaction PIN</h3>
            <p style={styles.modalSubtitle}>{pendingVtuTransaction.description}</p>
            <input 
              type="password" 
              value={vtuPin}
              onChange={(e) => setVtuPin(e.target.value)}
              placeholder="Enter 4-digit PIN" 
              maxLength={6} 
              style={styles.pinInput} 
              autoFocus
            />
            <div style={styles.modalButtons}>
              <button onClick={() => { setShowVtuPinModal(false); setPendingVtuTransaction(null); setVtuPin(""); }} style={styles.cancelBtn}>
                Cancel
              </button>
              <button onClick={handleVtuPinSubmit} style={styles.confirmBtn}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", backgroundColor: "#f0f2f5", fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
  loadingScreen: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", backgroundColor: "#f0f2f5" },
  loadingSpinner: { width: "40px", height: "40px", border: "4px solid #e9ecef", borderTop: "4px solid #007bff", borderRadius: "50%", animation: "spin 1s linear infinite" },
  header: { backgroundColor: "#1a1a2e", color: "white", padding: "15px 30px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  headerLeft: { display: "flex", alignItems: "center", gap: "15px" },
  logo: { margin: 0, fontSize: "22px", fontWeight: "700" },
  portalBadge: { backgroundColor: "#007bff", padding: "4px 12px", borderRadius: "15px", fontSize: "11px", fontWeight: "500" },
  headerRight: { display: "flex", alignItems: "center", gap: "20px" },
  welcomeText: { fontSize: "14px", opacity: 0.9 },
  logoutBtn: { padding: "8px 16px", backgroundColor: "transparent", border: "1px solid rgba(255,255,255,0.3)", color: "white", borderRadius: "5px", cursor: "pointer", fontSize: "13px" },
  nav: { backgroundColor: "white", padding: "0 30px", display: "flex", gap: "5px", borderBottom: "1px solid #e9ecef", overflowX: "auto" },
  navItem: { padding: "15px 20px", border: "none", backgroundColor: "transparent", cursor: "pointer", fontSize: "14px", color: "#666", borderBottom: "3px solid transparent", whiteSpace: "nowrap" },
  navItemActive: { color: "#007bff", borderBottomColor: "#007bff", fontWeight: "600" },
  main: { padding: "30px", maxWidth: "1000px", margin: "0 auto" },
  dashboard: { display: "flex", flexDirection: "column", gap: "25px" },
  accountCard: { background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)", color: "white", borderRadius: "16px", padding: "30px", boxShadow: "0 10px 40px rgba(26, 26, 46, 0.3)" },
  accountCardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" },
  accountLabel: { fontSize: "13px", opacity: 0.8 },
  accountType: { fontSize: "11px", backgroundColor: "rgba(255,255,255,0.2)", padding: "4px 10px", borderRadius: "10px" },
  accountBalance: { fontSize: "36px", fontWeight: "700", marginBottom: "25px" },
  accountInfo: { display: "flex", justifyContent: "space-between", paddingTop: "20px", borderTop: "1px solid rgba(255,255,255,0.1)" },
  accountInfoLabel: { display: "block", fontSize: "11px", opacity: 0.7, marginBottom: "5px" },
  accountNumber: { fontFamily: "monospace", fontSize: "16px", letterSpacing: "1px" },
  accountName: { fontSize: "14px" },
  quickActions: { backgroundColor: "white", borderRadius: "12px", padding: "25px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  sectionTitle: { margin: "0 0 20px", fontSize: "16px", fontWeight: "600", color: "#1a1a2e" },
  actionGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "15px" },
  actionBtn: { display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", padding: "20px 15px", backgroundColor: "#f8f9fa", border: "none", borderRadius: "12px", cursor: "pointer", transition: "all 0.2s" },
  actionIcon: { fontSize: "28px" },
  recentSection: { backgroundColor: "white", borderRadius: "12px", padding: "25px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" },
  viewAllBtn: { backgroundColor: "transparent", border: "none", color: "#007bff", cursor: "pointer", fontSize: "13px" },
  transactionList: { display: "flex", flexDirection: "column" },
  transactionItem: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px 0", borderBottom: "1px solid #f0f0f0" },
  txLeft: { display: "flex", alignItems: "center", gap: "15px" },
  txIcon: { width: "40px", height: "40px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", fontWeight: "bold" },
  txDescription: { fontWeight: "500", fontSize: "14px", color: "#333" },
  txDate: { fontSize: "12px", color: "#999", marginTop: "3px" },
  txAmount: { fontWeight: "600", fontSize: "15px" },
  transferSection: { maxWidth: "500px", margin: "0 auto" },
  pageTitle: { margin: "0 0 10px", fontSize: "22px", fontWeight: "600", color: "#1a1a2e" },
  pageSubtitle: { margin: "0 0 25px", fontSize: "14px", color: "#666" },
  transferCard: { backgroundColor: "white", borderRadius: "12px", padding: "30px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  balanceReminder: { backgroundColor: "#e8f4fd", padding: "12px 15px", borderRadius: "8px", marginBottom: "25px", fontSize: "14px", color: "#0066cc" },
  formGroup: { marginBottom: "20px" },
  label: { display: "block", marginBottom: "8px", fontSize: "13px", fontWeight: "500", color: "#333" },
  input: { width: "100%", padding: "12px 15px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
  inputWrapper: { position: "relative" },
  searchingIndicator: { position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)" },
  spinner: { width: "20px", height: "20px", border: "2px solid #ddd", borderTopColor: "#007bff", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  helperText: { fontSize: "12px", color: "#888", marginTop: "6px", marginBottom: "0" },
  radioGroup: { display: "flex", gap: "20px" },
  radioLabel: { display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", cursor: "pointer" },
  recipientCard: { display: "flex", alignItems: "center", gap: "15px", padding: "15px", backgroundColor: "#d4edda", borderRadius: "8px", marginBottom: "20px" },
  recipientIcon: { width: "40px", height: "40px", backgroundColor: "#28a745", color: "white", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", fontWeight: "bold" },
  recipientName: { fontWeight: "600", fontSize: "15px", color: "#155724" },
  recipientAccount: { fontSize: "13px", color: "#155724", opacity: 0.8 },
  transferBtn: { width: "100%", padding: "15px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "8px", fontSize: "16px", fontWeight: "600", cursor: "pointer" },
  transactionsSection: {},
  transactionsCard: { backgroundColor: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "13px" },
  th: { textAlign: "left", padding: "12px 10px", borderBottom: "2px solid #e9ecef", fontWeight: "600", color: "#666" },
  td: { padding: "12px 10px", borderBottom: "1px solid #f0f0f0" },
  typeBadge: { padding: "4px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "500" },
  
  // Statement styles
  statementSection: { maxWidth: "600px", margin: "0 auto" },
  statementCard: { backgroundColor: "white", borderRadius: "12px", padding: "30px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  quickSelectSection: { marginBottom: "25px" },
  quickSelectGrid: { display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "10px" },
  quickSelectBtn: { padding: "8px 16px", backgroundColor: "#f0f2f5", border: "1px solid #ddd", borderRadius: "20px", cursor: "pointer", fontSize: "13px", transition: "all 0.2s" },
  dateRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "25px" },
  dateField: {},
  errorBox: { backgroundColor: "#f8d7da", color: "#721c24", padding: "12px 15px", borderRadius: "8px", marginBottom: "20px", fontSize: "14px" },
  successBox: { backgroundColor: "#d4edda", color: "#155724", padding: "12px 15px", borderRadius: "8px", marginBottom: "20px", fontSize: "14px" },
  downloadBtn: { width: "100%", padding: "15px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "8px", fontSize: "16px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" },
  btnSpinner: { width: "20px", height: "20px", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%", animation: "spin 0.8s linear infinite", display: "inline-block" },
  statementNote: { marginTop: "20px", fontSize: "12px", color: "#888", textAlign: "center" },
  
  settingsSection: { maxWidth: "600px", margin: "0 auto" },
  settingsCard: { backgroundColor: "white", borderRadius: "12px", padding: "25px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", marginBottom: "20px" },
  settingsTitle: { margin: "0 0 20px", fontSize: "16px", fontWeight: "600", color: "#1a1a2e", paddingBottom: "15px", borderBottom: "1px solid #e9ecef" },
  settingsItem: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f5f5f5" },
  settingsLabel: { fontSize: "14px", color: "#666" },
  settingsValue: { fontSize: "14px", fontWeight: "500" },
  statusBadge: { padding: "4px 12px", borderRadius: "12px", fontSize: "12px", fontWeight: "500" },
  settingsBtn: { padding: "12px 20px", backgroundColor: "#f8f9fa", border: "1px solid #ddd", borderRadius: "8px", cursor: "pointer", fontSize: "14px", width: "100%", textAlign: "left" },
  noData: { textAlign: "center", padding: "40px", color: "#999" },
  modalOverlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { backgroundColor: "white", borderRadius: "16px", padding: "30px", width: "400px", maxWidth: "90%", textAlign: "center" },
  modalTitle: { margin: "0 0 10px", fontSize: "20px" },
  modalSubtitle: { color: "#666", fontSize: "14px", marginBottom: "25px" },
  pinInput: { width: "100%", padding: "15px", fontSize: "24px", textAlign: "center", border: "2px solid #ddd", borderRadius: "8px", letterSpacing: "10px", boxSizing: "border-box" },
  modalButtons: { display: "flex", gap: "15px", marginTop: "25px" },
  cancelBtn: { flex: 1, padding: "12px", backgroundColor: "#f8f9fa", border: "1px solid #ddd", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  confirmBtn: { flex: 1, padding: "12px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "600" },
  
  // Special Access Section Styles
  specialAccessSection: { backgroundColor: "#fef3c7", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: "2px solid #f59e0b" },
  rolesBar: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px", flexWrap: "wrap" },
  rolesLabel: { fontWeight: "600", color: "#92400e", fontSize: "13px" },
  roleBadge: { backgroundColor: "#4f46e5", color: "white", padding: "4px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: "500" },
  specialActionsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "10px" },
  specialBtn: { padding: "12px 15px", color: "white", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" },

  // Bills styles
  billsSection: { maxWidth: "600px", margin: "0 auto" },
  billTabs: { display: "flex", gap: "10px", marginBottom: "25px", flexWrap: "wrap" },
  billTab: { padding: "12px 24px", backgroundColor: "#f0f2f5", border: "none", borderRadius: "25px", cursor: "pointer", fontSize: "14px", fontWeight: "500", transition: "all 0.2s" },
  billTabActive: { backgroundColor: "#007bff", color: "white" },
  billCard: { backgroundColor: "white", borderRadius: "12px", padding: "30px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  billCardTitle: { margin: "0 0 25px", fontSize: "18px", fontWeight: "600", color: "#1a1a2e" },
  payBtn: { width: "100%", padding: "15px", backgroundColor: "#28a745", color: "white", border: "none", borderRadius: "8px", fontSize: "16px", fontWeight: "600", cursor: "pointer", marginTop: "10px" },
  successCard: { backgroundColor: "white", borderRadius: "12px", padding: "40px 30px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", textAlign: "center" },
  successIcon: { fontSize: "60px", marginBottom: "15px" },
  successTitle: { margin: "0 0 25px", fontSize: "22px", fontWeight: "600", color: "#28a745" },
  tokenDisplay: { backgroundColor: "#e8f5e9", borderRadius: "12px", padding: "25px", marginBottom: "25px" },
  tokenLabel: { display: "block", fontSize: "13px", color: "#666", marginBottom: "10px" },
  tokenValue: { display: "block", fontSize: "28px", fontWeight: "700", color: "#1a1a2e", letterSpacing: "2px", wordBreak: "break-all" },
  resultDetails: { textAlign: "left", backgroundColor: "#f8f9fa", borderRadius: "8px", padding: "20px", marginBottom: "25px" },
  resultRow: { display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #e9ecef", fontSize: "14px" },
  successActions: { display: "flex", gap: "15px", flexDirection: "column" },
  downloadBtn: { padding: "15px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "8px", fontSize: "16px", fontWeight: "600", cursor: "pointer" },
  newPaymentBtn: { padding: "15px", backgroundColor: "#f0f2f5", color: "#333", border: "none", borderRadius: "8px", fontSize: "16px", fontWeight: "500", cursor: "pointer" }
};











































